import React from "react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Brain, Code, BarChart3, Settings, Zap, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";

export default function ServicesSection({ texts }) {
  const services = [
    { icon: Brain, emoji: "🧠", ...texts.items[0], color: "from-purple-500 to-pink-500" },
    { icon: Code, emoji: "💻", ...texts.items[1], color: "from-blue-500 to-cyan-500" },
    { icon: Settings, emoji: "⚙️", ...texts.items[2], color: "from-green-500 to-teal-500" },
    { icon: BarChart3, emoji: "📊", ...texts.items[3], color: "from-orange-500 to-red-500" },
    { icon: Zap, emoji: "⚡", ...texts.items[4], color: "from-indigo-500 to-purple-500" }
  ];

  return (
    <section id="services" className="py-24 bg-black relative z-10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <Badge className="bg-gray-800 text-cyan-400 border-cyan-500/20 mb-4">
            {texts.badge}
          </Badge>
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              {texts.title1}
            </span>
            <br />
            <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              {texts.title2}
            </span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            {texts.subtitle}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-8 items-stretch">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
              className={index === 4 ? "lg:col-span-2 xl:col-span-1" : ""}
            >
              <Card className="h-full bg-gradient-to-b from-gray-900/80 to-gray-950/80 border-gray-800 hover:border-gray-700 transition-all duration-500 group rounded-2xl">
                <CardContent className="px-10 pb-10 pt-10 h-full flex flex-col">
                  <div className="flex items-center space-x-4 mb-8">
                    <div className={`p-3 rounded-xl bg-gradient-to-r ${service.color} bg-opacity-10 border border-gray-700`}>
                      <span className="text-2xl">{service.emoji}</span>
                    </div>
                    <h3 className="text-xl font-bold text-white group-hover:text-blue-300 transition-colors duration-300">
                      {service.title}
                    </h3>
                  </div>
                  
                  <p className="text-gray-400 mb-8 leading-relaxed flex-grow">
                    {service.description}
                  </p>
                  
                  <div className="space-y-3">
                    {service.benefits.map((benefit) => (
                      <div key={benefit} className="flex items-center space-x-3">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-sm text-gray-300">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Process Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mt-24"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">{texts.processTitle}</span>
            </h3>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              {texts.processSubtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {texts.processSteps.map((phase, index) => (
              <motion.div
                key={phase.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full flex items-center justify-center font-bold text-lg text-white">
                  {phase.step}
                </div>
                <h4 className="text-xl font-semibold text-white mb-2">{phase.title}</h4>
                <p className="text-gray-400 text-sm">{phase.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
